import { Component, OnInit } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import { OfficeService } from "src/app/services/office.service";
import { PersonService } from "src/app/services/person.service";
import { SuppliersService } from "../../services/suppliers.service";
import { ContractType, PersonType, SupplierRequest } from "../../supplier.model";
import { ResponsibilitiesService } from "src/app/features/responsibilities/services/responsibilities.service";
import { ErrorHandlerService } from "src/app/shared/error-handler.service";
import { LoggerService } from "@shared/logger.service";

@Component({
  selector: "app-suppliers-form",
  templateUrl: "./suppliers-form.component.html",
  styleUrls: ["./suppliers-form.component.css"],
})
export class SuppliersFormComponent implements OnInit {
  isSaving = false;
  imageUrl: string | ArrayBuffer | null = null;
  selectedFile: File | null = null;
  formGroup: FormGroup;
  offices: any[] = [];
  responsibilities: any[] = [];
  supervisors: any[] = [];
  managers: any[] = [];
  roles: any[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private personService: PersonService,
    private officeService: OfficeService,
    private responsibilitiesService: ResponsibilitiesService,
    private route: ActivatedRoute,
    private toast: ToastrService,
    private suppliersService: SuppliersService,
    private errorHandlerService: ErrorHandlerService,
    private logger: LoggerService
  ) {}

  ngOnInit(): void {
    this.buildFormGroup();

    this._offices();
    this._managers();
    this._supervisors();

    const id = this.route.snapshot.params["id"];
    if (id) {
      this.personService.findById(id).subscribe({
        next: (response) => {
          this.formGroup.get("id").patchValue(id);
          this.formGroup.get("name").patchValue(response.name);
          this.formGroup.get("email").patchValue(response.email);
          // CPF/CNPJ, Telefones e Endereço removidos - não são mais necessários
          this.formGroup.get("contractType").patchValue(response.contractType);
          this.formGroup.get("personType").patchValue(response.personType);
          this.formGroup.get("username").patchValue(response.username);
          this.formGroup.get("officeId").patchValue(response.officeId);
          this._responsibilities(response.officeId);
          this.formGroup.get("responsibilityId").patchValue(response.responsibilityId);
          this.formGroup.get("accessType").patchValue(response.accessType);
          this._managers();
          this.formGroup.get("managerId").patchValue(response.managerId);
          this._supervisors();
          this.formGroup.get("supervisorId").patchValue(response.supervisorId);
          this.imageUrl = response.picture;
        },
        error: (er) => this.logger.error("Error loading image", er),
      });
    }
    // Subscription de CEP removida - endereço não é mais necessário
  }

  private buildFormGroup(): void {
    this.formGroup = this.fb.group({
      id: [""],
      username: ["", Validators.required],
      email: ["", [Validators.required, Validators.email]],
      password: ["", Validators.minLength(6)],
      confirmPassword: ["", Validators.minLength(6)],
      // Campos removidos: cep, city, complement, neighborhood, streetName, uf, phone, cellphone, cpfCnpj
      name: ["", Validators.required],
      picture: [null],
      contractType: [""],
      personType: [""],
      office: [],
      responsibility: [],
      officeId: ["", Validators.required],
      responsibilityId: ["", Validators.required],
      managerId: [""],
      supervisorId: [""],
      accessType: ["", Validators.required],
    });
  }


  onSubmit() {
    this.isSaving = true;

    if (!this.formGroup.valid) {
      this.toast.error("O formulário para cadastro não é válido. Alguma informação deve estar errada.");
      return;
    }

    if (this.formGroup.get("password").value !== this.formGroup.get("confirmPassword").value) {
      this.toast.error("As senhas devem ser iguais.");
      return;
    }

    const supplierRequest = this.buildSupplierRequest();

    if (this.formGroup.get("id").value != "") {
      this.updateSupplier(supplierRequest);
      return;
    }

    this.createSupplier(supplierRequest);
  }

  private createSupplier(supplierRequest: SupplierRequest): void {
    this.suppliersService.create(supplierRequest, this.selectedFile).subscribe({
      next: () => {
        this.router.navigate(["/prestadores-de-servico"]).then((success) => {
          if (success) {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        });
        this.isSaving = false;
        this.toast.success("🎉 Fornecedor salvo com sucesso!");
      },
      error: (error: Error) => {
        this.isSaving = false;
        this.errorHandlerService.handle(error, "Erro ao criar prestador de serviço.");
      },
      complete: () => {
        this.formGroup.reset();
      },
    });
  }

  private updateSupplier(supplierRequest: SupplierRequest): void {
    this.suppliersService.update(this.formGroup.get("id").value, supplierRequest, this.selectedFile).subscribe({
      next: () => {
        this.router.navigate(["/prestadores-de-servico"]).then((success) => {
          if (success) {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        });
        this.isSaving = false;
        this.toast.success("🎉 Fornecedor alterado com sucesso!");
      },
      error: (error: Error) => {
        this.isSaving = false;
        this.errorHandlerService.handle(error, "Erro ao alterar prestador de serviço.");
      },
      complete: () => {
        this.formGroup.reset();
      },
    });
  }

  private buildSupplierRequest(): SupplierRequest {
    switch (this.formGroup.get("accessType")?.value) {
      case "Manager":
        this.formGroup.get("personType").patchValue(PersonType.MANAGER);
        break;
      case "Supervisor":
        this.formGroup.get("personType").patchValue(PersonType.SUPERVISOR);
        break;
      case "User":
        this.formGroup.get("personType").patchValue(PersonType.USER);
        break;
      default:
        this.formGroup.get("personType").patchValue(PersonType.USER);
    }
    return {
      name: this.formGroup.get("name")?.value,
      cpfCnpj: "",
      officeId: this.formGroup.get("officeId")?.value,
      responsibilityId: this.formGroup.get("responsibilityId")?.value,
      managerId: this.formGroup.get("managerId")?.value,
      supervisorId: this.formGroup.get("supervisorId")?.value,
      accessType: this.formGroup.get("accessType")?.value,
      personType: this.formGroup.get("personType")?.value,
      contractType: ContractType.SUPPLIER,
      username: this.formGroup.get("username")?.value,
      email: this.formGroup.get("email")?.value,
      password: this.formGroup.get("password")?.value,
      roles: this.roles,
    };
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];

    if (file) {
      this.selectedFile = file;
      this.formGroup.patchValue({
        photo: file,
      });

      const reader = new FileReader();
      reader.onload = (e) => {
        this.imageUrl = e.target?.result;
      };
      reader.readAsDataURL(file);
    }
  }

  _offices() {
    this.officeService.findAll().subscribe({
      next: (response: any[]) => {
        this.offices = response;
      },
      error: (err) => {
        this.logger.error("Error in form submission", err);
      },
    });
  }

  _responsibilities(officeId: string) {
    this.responsibilitiesService.findByOffice(officeId).subscribe({
      next: (response: any[]) => (this.responsibilities = response),
      error: (err) => this.logger.error("Error loading responsibilities", err),
    });
  }

  _managers(): void {
    this.personService.findAllManagers().subscribe({
      next: (response: any[]) => (this.managers = response),
      error: (err: any) => this.logger.error("Error loading managers", err),
    });
  }

  _supervisors(): void {
    this.personService.findAllSupervisors().subscribe({
      next: (response: any[]) => (this.supervisors = response),
      error: (err) => this.logger.error("Error loading supervisors", err),
    });
  }

  onOfficeChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("officeId").patchValue(value);
    
    // Limpar o cargo quando a empresa mudar
    this.formGroup.get("responsibilityId").patchValue("");
    
    // Carregar responsabilidades da nova empresa
    if (value) {
      this._responsibilities(value);
    } else {
      // Se não houver empresa selecionada, limpar a lista de responsabilidades
      this.responsibilities = [];
    }
  }

  onResponsibilityChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("responsibilityId").patchValue(value);
  }

  onSupervisorChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("supervisorId").patchValue(value);
  }

  onManagerChange(event: any): void {
    const value = event.value || event.target?.value;
    this.formGroup.get("managerId").patchValue(value);
  }

  onInputChange(): void {
    const usernameControl = this.formGroup.get("username");
    usernameControl?.setValue(usernameControl.value.replace(/\s/g, ""));
  }
}
